import {jwtDecode} from 'jwt-decode'
import {getAccessToken, getAuthMode} from './auth'
import {EXTERNAL_REFRESH_URL} from "../env"

/**
 * Decode JWT and extract expiration time
 * @param {string} token - JWT token
 * @returns {number|null} - Expiration timestamp in seconds, or null if invalid
 */
function getTokenExpiration(token: string): number | null {
  try {
    const decoded = jwtDecode(token)
    return decoded.exp || null
  } catch (e) {
    console.warn('Failed to decode token:', e)
    return null
  }
}

/**
 * Check if token is expired or will expire soon
 * @param {string} token - JWT token
 * @param {number} bufferSeconds - Seconds before expiry to consider "expiring soon"
 * @returns {boolean} - True if token is expired or expiring soon
 */
export function isTokenExpiringSoon(token: string, bufferSeconds: number = 300): boolean {
  const exp = getTokenExpiration(token)
  if (!exp) return true // Invalid token, treat as expired

  const nowSeconds = Math.floor(Date.now() / 1000)
  const timeUntilExpiry = exp - nowSeconds

  return timeUntilExpiry <= bufferSeconds
}

/**
 * Refresh the session token by calling leadiq-web-app's refresh endpoint
 * This uses CORS and browser's automatic cookie handling
 *
 * @returns {Promise<boolean>} - True if refresh succeeded, false otherwise
 */
export async function refreshToken(): Promise<boolean> {
  const authMode = getAuthMode()
  if (authMode !== 'external') {
    console.warn('Token refresh only available in external auth mode')
    return false
  }

  const refreshUrl = EXTERNAL_REFRESH_URL
  if (!refreshUrl) {
    console.error('VITE_EXTERNAL_REFRESH_URL not configured')
    return false
  }

  try {
    console.log('[TokenRefresh] Attempting to refresh session...')

    const response = await fetch(refreshUrl, {
      method: 'GET',
      credentials: 'include', // CRITICAL: Include cookies (LIQ_Refresh, LIQ_Session)
      headers: {
        'Accept': 'application/json',
      },
      // Don't set Content-Type for GET requests
    })

    if (!response.ok) {
      console.error('[TokenRefresh] Failed:', response.status, response.statusText)
      return false
    }

    // Server sets new LIQ_Session cookie via Set-Cookie header
    // Browser automatically stores it
    console.log('[TokenRefresh] Session refreshed successfully')

    // Extract and validate new token
    const newToken = getAccessToken()
    if (!newToken) {
      console.error('[TokenRefresh] No token found after refresh')
      return false
    }

    // Verify new token is valid and not expired
    const exp = getTokenExpiration(newToken)
    if (!exp || exp <= Math.floor(Date.now() / 1000)) {
      console.error('[TokenRefresh] New token is invalid or expired')
      return false
    }

    return true
  } catch (error) {
    console.error('[TokenRefresh] Error during refresh:', error)
    return false
  }
}

/**
 * Refresh token with lock to prevent parallel refresh attempts
 * Uses Web Locks API (same as AuthClient)
 *
 * @returns {Promise<boolean>} - True if refresh succeeded
 */
export async function refreshTokenWithLock(): Promise<boolean> {
  if (!navigator.locks) {
    // Fallback for browsers without Web Locks API
    console.warn('[TokenRefresh] Web Locks API not available, refreshing without lock')
    return refreshToken()
  }

  const LOCK_TIMEOUT = 30000 // 30 seconds

  try {
    const timeoutPromise = new Promise<boolean>((_, reject) => {
      setTimeout(() => reject(new Error('Lock timeout')), LOCK_TIMEOUT)
    })

    const refreshPromise = navigator.locks.request('raven_token_refresh_lock', async () => {
      console.log('[TokenRefresh] Lock acquired')
      return await refreshToken()
    })

    return await Promise.race([refreshPromise, timeoutPromise])
  } catch (error) {
    console.error('[TokenRefresh] Failed to acquire lock or refresh:', error)
    return false
  }
}

/**
 * Proactively check and refresh token if expiring soon
 * Call this on app startup and periodically
 *
 * @returns {Promise<void>}
 */
export async function checkAndRefreshToken(): Promise<void> {
  const authMode = getAuthMode()
  if (authMode !== 'external') return

  const token = getAccessToken()
  if (!token) {
    console.log('[TokenRefresh] No token to refresh')
    return
  }

  if (isTokenExpiringSoon(token, 300)) { // 5 minutes buffer
    console.log('[TokenRefresh] Token expiring soon, refreshing...')
    await refreshTokenWithLock()
  } else {
    console.log('[TokenRefresh] Token still valid, no refresh needed')
  }
}
